import { inject, Injectable, signal } from '@angular/core';
import { UntilDestroy } from '@ngneat/until-destroy';
import { HotToastService } from '@ngxpert/hot-toast';

import { ErrorCodes } from '@reach/interfaces/error/error-codes.enum';
import {
	CreateNodeCategoryRequest,
	UpdateNodeCategoryRequest,
} from '@reach/interfaces/node-categories/node-categories.requests';
import { NodeCategoryInterface } from '@reach/interfaces/node-categories/node-categories.types';

import { NodeCategoriesApiService } from '~app-client-item/api/services/node-categories.api.service';

export enum NodeCategoriesError {
	LOAD_CATEGORIES = 'LOAD_CATEGORIES',
	CREATE_CATEGORY = 'CREATE_CATEGORY',
	UPDATE_CATEGORY = 'UPDATE_CATEGORY',
	DELETE_CATEGORY = 'DELETE_CATEGORY',
	CATEGORY_DUPLICATE_NAME = 'CATEGORY_DUPLICATE_NAME',
}

@UntilDestroy()
@Injectable({ providedIn: 'root' })
export class NodeCategoriesService {
	private readonly api = inject(NodeCategoriesApiService);
	private readonly hotToastService = inject(HotToastService);
	public readonly $categories = signal<NodeCategoryInterface[]>([]);
	public readonly $categorySelected = signal<NodeCategoryInterface | null>(null);
	public readonly $loading = signal(false);

	public async loadCategories(): Promise<void> {
		this.$loading.set(true);
		try {
			const response = await this.api.getCategories();
			this.$categories.set(response);
		} catch (error) {
			const message = 'Failed to load categories';
			this.hotToastService.error(message);
			throw error;
		} finally {
			this.$loading.set(false);
		}
	}

	public async getCategory(id: string): Promise<NodeCategoryInterface> {
		this.$loading.set(true);
		try {
			const response = await this.api.getCategory(id);
			this.$categorySelected.set(response);
			this.hotToastService.success('Category loaded successfully');
			return response;
		} catch (error) {
			const message = 'Failed to load category';
			this.hotToastService.error(message);
			throw error;
		} finally {
			this.$loading.set(false);
		}
	}

	public async createCategory(body: CreateNodeCategoryRequest): Promise<NodeCategoryInterface> {
		this.$loading.set(true);
		try {
			const response = await this.api.createCategory(body);
			this.$categories.update((prev) => [...prev, response]);
			this.hotToastService.success('Category created successfully');
			return response;
		} catch (error) {
			if (error.error.code === ErrorCodes.NODE_CATEGORY_NAME_ALREADY_EXISTS) {
				this.hotToastService.error(error.error.title);
				throw error;
			} else {
				this.hotToastService.error('Failed to create category');
				throw error;
			}
		} finally {
			this.$loading.set(false);
		}
	}

	public async updateCategory(
		id: string,
		body: UpdateNodeCategoryRequest
	): Promise<NodeCategoryInterface> {
		this.$loading.set(true);
		try {
			const response = await this.api.updateCategory(id, body);
			this.$categories.update((prev) =>
				prev.map((category) => (category.id === id ? response : category))
			);
			this.hotToastService.success('Category updated successfully');
			return response;
		} catch (error) {
			const message = 'Failed to update category';
			this.hotToastService.error(message);
			throw error;
		} finally {
			this.$loading.set(false);
		}
	}

	public async deleteCategory(id: string): Promise<void> {
		this.$loading.set(true);
		try {
			await this.api.deleteCategory(id);
			this.$categories.update((prev) => prev.filter((category) => category.id !== id));
			this.hotToastService.success('Category deleted successfully');
		} catch (error) {
			const message = 'Failed to delete category';
			this.hotToastService.error(message);
			throw error;
		} finally {
			this.$loading.set(false);
		}
	}

	public setCategorySelected(category: NodeCategoryInterface | null): void {
		this.$categorySelected.set(category);
	}
}
